import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navigation from '../../Layouts/Navigation/Navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWater, faGear, faCalendarDays, faPlus, faBan,
  faCheckCircle, faTimesCircle, faClock, faBuilding, faSave, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../utils/constants';

const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DIAS_LOWER = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/* ── helpers ── */
const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.substring(0, 5).split(':').map(Number);
  const period = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

const fmtDate = (d) => {
  if (!d) return '';
  const s = typeof d === 'string' && d.includes('T') ? d.split('T')[0] : String(d);
  const [y, mo, day] = s.split('-');
  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${parseInt(day)} ${months[parseInt(mo) - 1]} ${y}`;
};

const parseDias = (str) => {
  if (!str) return [];
  return str.split(',').map(d => d.trim().toLowerCase());
};

const serializeDias = (arr) => arr.join(', ');

const toWeekdayNumbers = (str) =>
  parseDias(str).map(name => DIAS_LOWER.findIndex(d => d === name)).filter(i => i >= 0);

const isServiceDay = (dateStr, config) => {
  if (!dateStr || !config) return true;
  const [y, m, d] = dateStr.split('-').map(Number);
  const dow = new Date(y, m - 1, d).getDay();
  const allowed = toWeekdayNumbers(config.dias_servicio);
  if (allowed.length === 0) return true;
  return allowed.includes(dow);
};

const canCreateReservation = (config) => {
  if (!config) return false;
  const now = new Date();
  if (now.getDay() !== config.dia_reserva) return false;
  if (!config.hora_inicio_reserva) return true;
  const [bh, bm] = config.hora_inicio_reserva.substring(0, 5).split(':').map(Number);
  return now.getHours() * 60 + now.getMinutes() >= bh * 60 + bm;
};

const todayStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const nowTimeStr = () => {
  const n = new Date();
  return `${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`;
};

const Snack = ({ msg, type, onClose }) => {
  if (!msg) return null;
  const colors = { success: 'bg-green-500', error: 'bg-red-500', info: 'bg-blue-500' };
  return (
    <div className={`fixed top-5 right-5 z-50 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 ${colors[type] || colors.info}`}>
      <span>{msg}</span>
      <button onClick={onClose} className="ml-2 font-bold">✕</button>
    </div>
  );
};

const AdminPool = () => {
  const [tab, setTab] = useState('reservas');
  const [config, setConfig] = useState(null);
  const [editConfig, setEditConfig] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [towers, setTowers] = useState([]);
  const [torreApartments, setTorreApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ msg: '', type: 'success' });

  // Form nueva reserva
  const [showForm, setShowForm] = useState(false);
  const [formDate, setFormDate] = useState('');
  const [formSlot, setFormSlot] = useState('');
  const [formTorre, setFormTorre] = useState('');
  const [formApto, setFormApto] = useState('');
  const [formUser, setFormUser] = useState('');
  const [formObs, setFormObs] = useState('');
  const [aptoUsers, setAptoUsers] = useState([]);
  const [filterDate, setFilterDate] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [dateError, setDateError] = useState('');
  const [aptMora, setAptMora] = useState(false);
  const [moraMensaje, setMoraMensaje] = useState('');

  const showMsg = (msg, type = 'success') => setSnack({ msg, type });

  const fetchConfig = useCallback(async () => {
    const r = await axios.get(`${BASE_URL}/amenities/pool/config`);
    setConfig(r.data);
    setEditConfig({ ...r.data });
  }, []);

  const fetchReservations = useCallback(async (fecha) => {
    const params = fecha ? { fecha } : {};
    const r = await axios.get(`${BASE_URL}/amenities/pool/reservations`, { params });
    setReservations(r.data);
  }, []);

  const fetchTowers = useCallback(async () => {
    const r = await axios.get(`${BASE_URL}/towers`);
    setTowers(r.data);
  }, []);

  useEffect(() => {
    Promise.all([fetchConfig(), fetchReservations(''), fetchTowers()])
      .finally(() => setLoading(false));
  }, [fetchConfig, fetchReservations, fetchTowers]);

  // Cargar disponibilidad cuando cambia la fecha del form
  useEffect(() => {
    if (!formDate) { setAvailability(null); return; }
    axios.get(`${BASE_URL}/amenities/pool/availability`, { params: { fecha: formDate } })
      .then(r => setAvailability(r.data))
      .catch(() => setAvailability(null));
  }, [formDate]);

  // Cargar apartamentos cuando cambia la torre
  useEffect(() => {
    if (!formTorre) { setTorreApartments([]); setFormApto(''); setFormUser(''); setAptoUsers([]); return; }
    axios.get(`${BASE_URL}/getApartmentsData/${formTorre}`)
      .then(r => setTorreApartments(r.data))
      .catch(() => setTorreApartments([]));
    setFormApto(''); setFormUser(''); setAptoUsers([]);
    setAptMora(false); setMoraMensaje('');
  }, [formTorre]);

  // Cargar usuarios por apartamento
  useEffect(() => {
    if (!formApto) { setAptoUsers([]); setFormUser(''); setAptMora(false); setMoraMensaje(''); return; }
    const apto = torreApartments.find(a => String(a.id_apartamento) === String(formApto));
    if (apto && apto.estado_cartera === 'en_mora') {
      setAptMora(true);
      setMoraMensaje(`El apartamento ${apto.num_apartamento} tiene pagos pendientes con administración. No se puede realizar esta reserva.`);
    } else {
      setAptMora(false);
      setMoraMensaje('');
    }
    axios.get(`${BASE_URL}/users/apartment/${formApto}`)
      .then(r => { setAptoUsers(r.data); if (r.data.length > 0) setFormUser(String(r.data[0].id_usuario)); })
      .catch(() => setAptoUsers([]));
    setFormUser('');
  }, [formApto, torreApartments]);

  const handleFilterDate = (d) => { setFilterDate(d); fetchReservations(d); };

  const handleCancel = async (id) => {
    if (!window.confirm('¿Cancelar esta reserva?')) return;
    try {
      await axios.put(`${BASE_URL}/amenities/pool/reservations/${id}/cancel`);
      showMsg('Reserva cancelada');
      fetchReservations(filterDate);
    } catch (e) {
      showMsg(e.response?.data?.error || 'Error al cancelar', 'error');
    }
  };

  const handleDateChange = (dateStr) => {
    setDateError('');
    if (!dateStr) { setFormDate(''); setFormSlot(''); return; }
    if (dateStr < todayStr()) {
      setDateError('No se puede reservar en una fecha pasada');
      setFormDate(''); setFormSlot(''); return;
    }
    if (!isServiceDay(dateStr, config)) {
      const allowed = toWeekdayNumbers(config?.dias_servicio || '').map(i => DIAS[i]).join(', ');
      setDateError(`Solo se permiten reservas los días: ${allowed || 'ninguno configurado'}`);
      setFormDate(''); setFormSlot('');
      return;
    }
    setFormDate(dateStr); setFormSlot('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formDate || !formSlot || !formApto || !formUser) {
      showMsg('Completa todos los campos requeridos', 'error'); return;
    }
    if (aptMora) { showMsg(moraMensaje, 'error'); return; }
    const slot = availability?.slots?.find(s => s.hora_inicio === formSlot);
    if (!slot) { showMsg('Selecciona un horario válido', 'error'); return; }
    try {
      await axios.post(`${BASE_URL}/amenities/pool/reservations`, {
        id_apartamento: formApto, id_usuario: formUser, fecha: formDate,
        hora_inicio: slot.hora_inicio, hora_fin: slot.hora_fin, observacion: formObs
      });
      showMsg('Reserva creada exitosamente');
      setShowForm(false);
      setFormDate(''); setFormSlot(''); setFormTorre(''); setFormApto('');
      setFormUser(''); setFormObs(''); setDateError('');
      fetchReservations(filterDate);
    } catch (e) {
      showMsg(e.response?.data?.error || 'Error al crear reserva', 'error');
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${BASE_URL}/amenities/pool/config`, editConfig);
      showMsg('Configuración guardada'); fetchConfig();
    } catch (e) {
      showMsg(e.response?.data?.error || 'Error al guardar', 'error');
    } finally { setSavingConfig(false); }
  };

  const toggleDia = (diaName) => {
    const current = parseDias(editConfig.dias_servicio);
    const norm = diaName.toLowerCase();
    const next = current.includes(norm) ? current.filter(d => d !== norm) : [...current, norm];
    setEditConfig(p => ({ ...p, dias_servicio: serializeDias(next) }));
  };

  const bookingAllowed = canCreateReservation(config);

  if (loading) return (
    <>
      <Navigation />
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-12 w-12 rounded-full border-b-2 border-blue-600"></div>
      </div>
    </>
  );

  return (
    <>
      <Navigation />
      <Snack msg={snack.msg} type={snack.type} onClose={() => setSnack({ msg: '' })} />

      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl shadow">
              <FontAwesomeIcon icon={faWater} className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Piscina — Gestión de Reservas</h1>
              {config && (
                <p className="text-sm text-gray-500 mt-1">
                  Servicio: {config.dias_servicio} · {fmtTime(config.hora_inicio)} – {fmtTime(config.hora_fin)} ·
                  Reservas abren: {DIAS[config.dia_reserva]} a las {fmtTime(config.hora_inicio_reserva)} ·
                  Cupos: {config.max_cupos} · {config.duracion_minutos} min/turno
                </p>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {[{ id: 'reservas', label: 'Reservas', icon: faCalendarDays },
              { id: 'config', label: 'Configuración', icon: faGear }].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all
                  ${tab === t.id ? 'bg-blue-600 text-white shadow' : 'bg-white text-gray-600 hover:bg-blue-50'}`}>
                <FontAwesomeIcon icon={t.icon} />
                {t.label}
              </button>
            ))}
          </div>

          {/* ── RESERVAS ───────────────────────────────────── */}
          {tab === 'reservas' && (
            <>
              {/* Acciones */}
              <div className="bg-white rounded-2xl shadow p-4 mb-4 flex flex-wrap gap-4 items-center justify-between">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCalendarDays} className="text-blue-500" />
                  <label className="text-sm font-medium text-gray-700">Filtrar por fecha:</label>
                  <input type="date" value={filterDate} onChange={e => handleFilterDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none" />
                  {filterDate && (
                    <button onClick={() => handleFilterDate('')}
                      className="text-xs text-gray-400 hover:text-red-500 transition-colors">Limpiar</button>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <button onClick={() => { if (bookingAllowed) setShowForm(!showForm); }}
                    disabled={!bookingAllowed}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors shadow
                      ${bookingAllowed ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                    <FontAwesomeIcon icon={faPlus} />
                    Nueva reserva
                  </button>
                  {!bookingAllowed && config && (
                    <p className="text-xs text-amber-600">
                      Reservas solo abren los {DIAS[config.dia_reserva]} desde las {fmtTime(config.hora_inicio_reserva)}
                    </p>
                  )}
                </div>
              </div>

              {/* Formulario nueva reserva */}
              {showForm && bookingAllowed && (
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-4">
                  <h3 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon={faPlus} />
                    Nueva reserva de piscina
                  </h3>
                  <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Fecha */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
                      <input type="date" value={formDate} onChange={e => handleDateChange(e.target.value)} required
                        min={todayStr()}
                        className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none ${dateError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />
                      {dateError && <p className="text-xs text-red-500 mt-1">{dateError}</p>}
                      {config && !dateError && (
                        <p className="text-xs text-gray-400 mt-1">
                          Días habilitados: {toWeekdayNumbers(config.dias_servicio).map(i => DIAS[i]).join(', ') || 'ninguno'}
                        </p>
                      )}
                    </div>

                    {/* Horario */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Horario disponible *</label>
                      <select value={formSlot} onChange={e => setFormSlot(e.target.value)} required disabled={!formDate}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400">
                        <option value="">{formDate ? 'Selecciona un horario' : 'Selecciona la fecha primero'}</option>
                        {availability?.slots?.filter(s => s.disponible && (formDate !== todayStr() || s.hora_inicio >= nowTimeStr())).map(s => (
                          <option key={s.hora_inicio} value={s.hora_inicio}>
                            {fmtTime(s.hora_inicio)} – {fmtTime(s.hora_fin)}
                          </option>
                        ))}
                      </select>
                      {formDate && availability && (
                        <p className="text-xs text-gray-500 mt-1">
                          {availability.slots?.filter(s => s.disponible && (formDate !== todayStr() || s.hora_inicio >= nowTimeStr())).length} de {availability.slots?.length} horarios disponibles
                        </p>
                      )}
                    </div>

                    {/* Torre */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <FontAwesomeIcon icon={faBuilding} className="mr-1 text-blue-400" />Torre *
                      </label>
                      <select value={formTorre} onChange={e => setFormTorre(e.target.value)} required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none">
                        <option value="">Selecciona una torre</option>
                        {towers.map(t => (
                          <option key={t.id_torre} value={t.id_torre}>{t.nombre}</option>
                        ))}
                      </select>
                    </div>

                    {/* Apartamento */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Apartamento *</label>
                      <select value={formApto} onChange={e => setFormApto(e.target.value)} required disabled={!formTorre}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400">
                        <option value="">{formTorre ? 'Selecciona un apartamento' : 'Selecciona torre primero'}</option>
                        {torreApartments.map(a => (
                          <option key={a.id_apartamento} value={a.id_apartamento}>Apto {a.num_apartamento}</option>
                        ))}
                      </select>
                    </div>

                    {/* Inquilino */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Inquilino *</label>
                      <select value={formUser} onChange={e => setFormUser(e.target.value)} required
                        disabled={!formApto || aptoUsers.length === 0}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400">
                        <option value="">
                          {!formApto ? 'Selecciona apartamento primero' : aptoUsers.length ? 'Selecciona un inquilino' : 'Sin inquilinos'}
                        </option>
                        {aptoUsers.map(u => (
                          <option key={u.id_usuario} value={u.id_usuario}>{u.nombre} {u.apellido}</option>
                        ))}
                      </select>
                    </div>

                    {/* Observación */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Observación</label>
                      <input type="text" value={formObs} onChange={e => setFormObs(e.target.value)}
                        placeholder="Opcional" maxLength={255}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none" />
                    </div>

                    {aptMora && (
                      <div className="md:col-span-2 flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-4 py-3">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-bold text-red-800">Reserva no permitida</p>
                          <p className="text-sm text-red-700 mt-0.5">{moraMensaje}</p>
                        </div>
                      </div>
                    )}
                    <div className="md:col-span-2 flex gap-3 justify-end">
                      <button type="button" onClick={() => { setShowForm(false); setDateError(''); }}
                        className="px-4 py-2 text-sm text-gray-600 hover:text-red-500 bg-gray-100 rounded-lg transition-colors">
                        Cancelar
                      </button>
                      <button type="submit"
                        disabled={aptMora}
                        className={`px-5 py-2 text-sm font-semibold rounded-lg transition-colors shadow ${aptMora ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
                        Crear reserva
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Tabla de reservas */}
              <div className="bg-white rounded-2xl shadow overflow-hidden">
                {reservations.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <FontAwesomeIcon icon={faCalendarDays} className="h-12 w-12 mb-3 opacity-30" />
                    <p className="text-lg font-medium">No hay reservas{filterDate ? ` para ${fmtDate(filterDate)}` : ''}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          {['Fecha', 'Horario', 'Torre', 'Apto', 'Inquilino', 'Teléfono', 'Estado', 'Acción'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {reservations.map(r => (
                          <tr key={r.id_reserva} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 font-medium">{fmtDate(r.fecha)}</td>
                            <td className="px-4 py-3">
                              <span className="flex items-center gap-1 text-blue-700">
                                <FontAwesomeIcon icon={faClock} className="h-3.5 w-3.5" />
                                {fmtTime(r.hora_inicio)} – {fmtTime(r.hora_fin)}
                              </span>
                            </td>
                            <td className="px-4 py-3">{r.torre_nombre}</td>
                            <td className="px-4 py-3 font-semibold">{r.num_apartamento}</td>
                            <td className="px-4 py-3">{r.inquilino_nombre} {r.inquilino_apellido}</td>
                            <td className="px-4 py-3 text-gray-500">{r.inquilino_telefono}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold
                                ${r.estado === 'confirmada' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                <FontAwesomeIcon icon={r.estado === 'confirmada' ? faCheckCircle : faTimesCircle} className="h-3 w-3" />
                                {r.estado}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {r.estado === 'confirmada' && (
                                <button onClick={() => handleCancel(r.id_reserva)}
                                  className="flex items-center gap-1 text-xs text-red-600 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition-colors border border-red-200">
                                  <FontAwesomeIcon icon={faBan} />
                                  Cancelar
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── CONFIGURACIÓN ───────────────────────────────── */}
          {tab === 'config' && editConfig && (
            <div className="bg-white rounded-2xl shadow p-6 max-w-2xl">
              <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                <FontAwesomeIcon icon={faGear} className="text-blue-500" />
                Configuración de la piscina
              </h3>

              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Días de servicio</label>
                    <div className="flex flex-wrap gap-2">
                      {DIAS.map((dia, i) => {
                        const checked = parseDias(editConfig.dias_servicio).includes(DIAS_LOWER[i]);
                        return (
                          <button type="button" key={i} onClick={() => toggleDia(dia)}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors
                              ${checked ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
                            {dia}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Seleccionado: {editConfig.dias_servicio || 'ninguno'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duración por turno (min)</label>
                    <input type="number" min={15} max={240} step={15}
                      value={editConfig.duracion_minutos}
                      onChange={e => setEditConfig(p => ({ ...p, duracion_minutos: parseInt(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hora de apertura (servicio)</label>
                    <input type="time" value={editConfig.hora_inicio ? editConfig.hora_inicio.substring(0, 5) : ''}
                      onChange={e => setEditConfig(p => ({ ...p, hora_inicio: e.target.value + ':00' }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hora de cierre (servicio)</label>
                    <input type="time" value={editConfig.hora_fin ? editConfig.hora_fin.substring(0, 5) : ''}
                      onChange={e => setEditConfig(p => ({ ...p, hora_fin: e.target.value + ':00' }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Día de apertura de reservas</label>
                    <select value={editConfig.dia_reserva}
                      onChange={e => setEditConfig(p => ({ ...p, dia_reserva: parseInt(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none">
                      {DIAS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hora de apertura de reservas</label>
                    <input type="time" value={editConfig.hora_inicio_reserva ? editConfig.hora_inicio_reserva.substring(0, 5) : ''}
                      onChange={e => setEditConfig(p => ({ ...p, hora_inicio_reserva: e.target.value + ':00' }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Máximo de cupos por día</label>
                    <input type="number" min={1} max={50}
                      value={editConfig.max_cupos}
                      onChange={e => setEditConfig(p => ({ ...p, max_cupos: parseInt(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none" />
                  </div>

                  <div className="flex items-center gap-3 pt-4">
                    <input type="checkbox" id="poolActivo" checked={!!editConfig.activo}
                      onChange={e => setEditConfig(p => ({ ...p, activo: e.target.checked ? 1 : 0 }))}
                      className="w-4 h-4 text-blue-600 rounded" />
                    <label htmlFor="poolActivo" className="text-sm font-medium text-gray-700">
                      Piscina activa (recibe reservas)
                    </label>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-100">
                  <button onClick={handleSaveConfig} disabled={savingConfig}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-6 py-2.5 rounded-xl font-semibold text-sm transition-colors shadow">
                    <FontAwesomeIcon icon={faSave} />
                    {savingConfig ? 'Guardando...' : 'Guardar configuración'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default AdminPool;
